import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { PortalProjectDirectoryItem } from "@mbbe/types";
import { useLocation, useSearchParams } from "react-router-dom";
import { useCustomerAuth } from "./CustomerAuthContext";
import { getPortalProjects } from "./lib/projectsApi";
import { hasPortalProjectGrant } from "./lib/portalAccess";
import {
  parseRequestedPortalProject,
  portalProjectScope,
  switchPortalProject,
  withPortalProject,
  type PortalProjectScope,
} from "./lib/portalProjectContext";

interface Directory {
  status: "absent" | "loading" | "ready" | "error";
  items: PortalProjectDirectoryItem[];
  /** True when the server holds more projects than the bounded directory returned. */
  truncated: boolean;
  isGranted: (projectId: string) => boolean;
  sessionKey: string;
}

const absentDirectory: Directory = { status: "absent", items: [], truncated: false, isGranted: () => true, sessionKey: "" };
const DirectoryContext = createContext<Directory>(absentDirectory);

/**
 * Loads the bounded project-name directory once for the authenticated portal
 * shell. The key carries the member and grant fingerprint, so a membership or
 * grant change never serves another session's project names.
 */
export function PortalProjectProvider({ children }: { children: ReactNode }) {
  const { me } = useCustomerAuth();
  const sessionKey = useMemo(
    () => [me?.uid ?? "", me?.access?.allProjects ? "all" : (me?.access?.projectIds ?? []).join(",")].join("|"),
    [me?.uid, me?.access?.allProjects, me?.access?.projectIds],
  );
  const query = useQuery({
    queryKey: ["portal-projects", sessionKey],
    queryFn: getPortalProjects,
    enabled: Boolean(me),
    staleTime: 60_000,
  });
  const items = query.data?.items;
  const value = useMemo<Directory>(
    () => ({
      status: query.isError && !items ? "error" : items ? "ready" : "loading",
      items: items ?? [],
      truncated: query.data?.truncated === true,
      isGranted: (projectId) => hasPortalProjectGrant(me, projectId),
      sessionKey,
    }),
    [items, me, query.data?.truncated, query.isError, sessionKey],
  );
  return <DirectoryContext.Provider value={value}>{children}</DirectoryContext.Provider>;
}

/**
 * none: no project requested. malformed: unusable value. loading: directory in
 * flight. ready: the project is authorized and named. unavailable: missing,
 * deleted, foreign or ungranted. unverified: the directory could not be read
 * (or was truncated); the server still enforces the filter on every request.
 */
export type PortalProjectStatus = "none" | "malformed" | "loading" | "ready" | "unavailable" | "unverified";

export interface PortalProjectState {
  scope: PortalProjectScope;
  status: PortalProjectStatus;
  /** Validated project id, only while the project may be used in requests. */
  projectId: string | undefined;
  project: PortalProjectDirectoryItem | null;
  projects: PortalProjectDirectoryItem[];
  /** True when a requested project exists but must not be shown or requested. */
  blocked: boolean;
  sessionKey: string;
  hrefFor: (to: string) => string;
  selectProject: (projectId: string | null) => void;
  /** True when a record belongs to a different project than the one in view. */
  isOutOfScope: (recordProjectId: string | null | undefined) => boolean;
  /** True when the signed-in member holds a grant for the project (always true for no project). */
  isGranted: (projectId: string | null | undefined) => boolean;
}

export function usePortalProject(): PortalProjectState {
  const { pathname } = useLocation();
  const [params, setParams] = useSearchParams();
  const directory = useContext(DirectoryContext);
  const scope = portalProjectScope(pathname);
  const requested = parseRequestedPortalProject(params);
  const requestedId = requested.kind === "valid" ? requested.id : undefined;

  const project = requestedId ? (directory.items.find((item) => item.id === requestedId) ?? null) : null;
  let status: PortalProjectStatus;
  if (requested.kind === "none") status = "none";
  else if (requested.kind === "malformed") status = "malformed";
  else if (!directory.isGranted(requestedId!)) status = "unavailable";
  else if (directory.status === "loading") status = "loading";
  else if (directory.status === "ready") {
    // A truncated directory cannot prove absence, so the server decides.
    status = project ? "ready" : directory.truncated ? "unverified" : "unavailable";
  }
  else status = "unverified";

  const blocked = status === "malformed" || status === "unavailable";
  const projectId = requestedId && !blocked ? requestedId : undefined;
  const carryId = scope !== "account" ? projectId : undefined;

  const hrefFor = useCallback(
    (to: string) => (carryId && portalProjectScope(to.split("?")[0]) !== "account" ? withPortalProject(to, carryId) : to),
    [carryId],
  );
  const searchKey = params.toString();
  const selectProject = useCallback(
    (next: string | null) => setParams(switchPortalProject(new URLSearchParams(searchKey), next)),
    [searchKey, setParams],
  );
  const isOutOfScope = useCallback(
    (recordProjectId: string | null | undefined) => Boolean(projectId && recordProjectId && recordProjectId !== projectId),
    [projectId],
  );

  return {
    scope, status, projectId, project, projects: directory.items, blocked, sessionKey: directory.sessionKey,
    hrefFor, selectProject, isOutOfScope,
    isGranted: (recordProjectId) => !recordProjectId || directory.isGranted(recordProjectId),
  };
}
